
















































































import {
computed, defineComponent, onMounted, Ref, ref, ssrRef, useContext, useFetch, useRoute, watch,
} from '@nuxtjs/composition-api';
import { SfHeading } from '@storefront-ui/vue';
import LazyHydrate from 'vue-lazy-hydration';
import SeoMeta from '~/components/Seo/SeoMeta.vue';
import SkeletonLoader from '~/components/SkeletonLoader/index.vue';
import {
MatchingBikeInfo,
useCategory, useFacet,
useFrametool,
useUiHelpers, useUiState,
} from '~/composables';
import CategoryPagination from '~/modules/catalog/category/components/pagination/CategoryPagination.vue';

import { useAddToCart } from '~/helpers/cart/addToCart';
import facetGetters from '~/modules/catalog/category/getters/facetGetters';
import { useTraverseCategory } from '~/modules/catalog/category/helpers/useTraverseCategory';
import { usePrice } from '~/modules/catalog/pricing/usePrice';

import CategoryBreadcrumbs from '~/modules/catalog/category/components/breadcrumbs/CategoryBreadcrumbs.vue';
import CategoryNavbar from '~/modules/catalog/category/components/navbar/CategoryNavbar.vue';

import type { Pagination } from '~/composables/types';
import { Routes, searchTermQueryParameter } from '~/helpers/routes';
import type { SortingModel } from '~/modules/catalog/category/composables/useFacet/sortingOptions';
import type { Aggregation, ProductInterface } from '~/modules/GraphQL/types';
import { useFiltersStore } from '../category/components/filters/stores/filtersStore';

interface ISlider {
  price?: { from: string, to: string },
  battery?: { from: string, to: string },
  manufacturerWeight?: { from: string, to: string },
}

export default defineComponent({
  name: 'ProductOverview',
  components: {
    CategoryPagination,
    CategoryEmptyResults: () => import('~/modules/catalog/category/components/CategoryEmptyResults.vue'),
    CategoryFilters: () => import('~/modules/catalog/category/components/filters/CategoryFilters.vue'),
    CategoryProductGrid: () => import('~/modules/catalog/category/components/views/CategoryProductGrid.vue'),
    CategoryNavbar,
    CategoryBreadcrumbs,
    LazyHydrate,
    SfHeading,
    SkeletonLoader,
    SeoMeta,
  },
  transition: 'fade',
  setup() {
    const route = useRoute();
    const { app: { i18n } } = useContext();
    const categorySlug = computed(() => route.value.params.slug);

    const { load, loadCategoryMeta } = useCategory();
    const uiHelpers = useUiHelpers();
    const products = ssrRef<ProductInterface[]>([]);
    const filters = ssrRef<Aggregation[]>([]);
    const sortBy = ref<SortingModel>({ selected: '', options: [] });
    const pagination = ref<Pagination>({});
    const activeCategoryName = ref<string>('');
    const activeSizeFilterValues = ref<string[]>([]);
    const manufacturerNames = ref<string[]>([]);

    const productContainerElement = ref<HTMLElement | null>(null);

    const { toggleFilterSidebar, isFilterSidebarOpen, toggleFramesizeModal } = useUiState();

    const { result, search } = useFacet();
    const { addItemToCart } = useAddToCart();

    const categoryMeta = ref(null);

    const { activeCategory, isBikeCategory, loadCategoryTree } = useTraverseCategory();
    const activeCategoryId = computed(() => activeCategory.value?.uid ?? '');

    watch(
      [() => activeCategory.value, () => uiHelpers.getFacetsFromURL(), () => filters.value],
      ([updatedCategory, updatedFacets, updatedFilters]) => {
        const currentCategoryName = updatedCategory?.name ?? '';
        const isManufacturerFilterActive = updatedFacets.filters.manufacturer?.length > 0;
        // if there is a manufacturer filter active, we want to display the manufacturer name in the category title
        if (isManufacturerFilterActive && isBikeCategory.value) {
          manufacturerNames.value = updatedFilters
            .find((filter) => filter.attribute_code === 'manufacturer')?.options
            ?.map((option) => option.label);

          const manufacturerName = manufacturerNames.value?.join(', ');

          const displayName = manufacturerNames.value?.length ? `${manufacturerName} - ` : '';
          activeCategoryName.value = `${displayName}${currentCategoryName}`;
        } else {
          activeCategoryName.value = currentCategoryName;
          manufacturerNames.value = [];
        }

        // get active size filter values
        activeSizeFilterValues.value = updatedFacets.filters.size ?? [];
        return null;
      },
      { deep: true },
    );

    const heading = route.value.name === 'search' ? i18n.t('Search') : activeCategoryName ?? i18n.t('Result');

    const filtersSliders: Ref<ISlider> = ref({});
    const filtersStore = useFiltersStore();
    const { getShowMatchingBikes, getMatchingBikes } = useFrametool();
    const showMatchingBikes = ref(false);

    const isMobile: boolean = false;

    const { fetch } = useFetch(async () => {
      if (!activeCategory.value) {
        await loadCategoryTree();
      }

      const searchParams = { ...uiHelpers.getFacetsFromURL(), category_uid: activeCategoryId.value };
      let showMatchingBikesSetting = false;

      if (process.client && isBikeCategory.value) {
        showMatchingBikesSetting = await getShowMatchingBikes();

        if (showMatchingBikesSetting) {
          const matchingBikes: MatchingBikeInfo[] = await getMatchingBikes();
          // we need to set at least one sku - if no matching bikes are found, we use a dummy sku
          // which would return 0 results from Magento
          searchParams.filters.sku = matchingBikes.length === 0 ? ['-1'] : matchingBikes.map((item) => item.sku);
        }
      }

      if (filtersSliders.value.price) {
        searchParams.filters.price = [`${filtersSliders.value.price.from}_${filtersSliders.value.price.to}`];
      }

      if (filtersSliders.value.battery) {
        searchParams.filters.battery = [`${filtersSliders.value.battery.from}_${filtersSliders.value.battery.to}`];
      }

      if (filtersSliders.value.manufacturerWeight) {
        searchParams.filters.manufacturer_weight = [`${filtersSliders.value.manufacturerWeight.from}_${filtersSliders.value.manufacturerWeight.to}`];
      }

      const [categoryMetaData] = await Promise.all([
        load({ pageSize: 20 }),
        loadCategoryMeta({ url_key: categorySlug.value }),
        search(searchParams),
      ]);

      categoryMeta.value = categoryMetaData;

      if (facetGetters.getFilters(result.value).length > 0) {
        filtersStore.$patch((state) => {
          state.filters = facetGetters.getFilters(result.value);
        });
      }

      products.value = facetGetters.getProducts(result.value) ?? [];

      // size filter should only show up for bike categories
      const itemFilters = filtersStore.filters ?? [];
      filters.value = isBikeCategory.value
        ? itemFilters
        : itemFilters.filter(({ attribute_code }) => attribute_code !== 'size');
      sortBy.value = facetGetters.getSortOptions(result.value);
      pagination.value = facetGetters.getPagination(result.value);

      // trigger filter update if necessary
      if (isBikeCategory.value) {
        showMatchingBikes.value = showMatchingBikesSetting;
      }
    });

    const isPriceLoaded = ref(false);

    onMounted(async () => {
      const { getPricesBySku } = usePrice();
      if (products.value.length > 0) {
        const skus = products.value.map((item) => item.sku);
        const priceData = await getPricesBySku(skus, pagination.value.itemsPerPage);
        products.value = products.value.map((product) => {
          const priceRange = priceData.items.find((item) => item.sku === product.sku)?.price_range;

          if (priceRange) {
            return {
              ...product,
              price_range: priceRange,
            };
          }

          return { ...product };
        });
      }

      isPriceLoaded.value = true;

      // this needs to happen on client side only
      // local storage is only available here
      // re-fetch matching bikes if setting is necessary
      if (isBikeCategory.value) {
        const showMatchingBikesSetting = await getShowMatchingBikes();
        if (showMatchingBikesSetting) {
          fetch();
        }
      }

      const framesizeParams = [
        'height',
        'steplength',
      ];

      const routeQueryParams = route.value.query;
      const isFramesizeParams = framesizeParams.every((param) => Object.keys(routeQueryParams).includes(param));
      if (isFramesizeParams) {
        toggleFramesizeModal();
      }
    });

    const onReloadProducts = () => {
      fetch();
      productContainerElement.value.scrollIntoView();
    };

    const goToPage = (page: number, forcePush = false) => {
      uiHelpers.changePage(page, forcePush);
      onReloadProducts();
    };

    const doChangeItemsPerPage = (itemsPerPage: number) => {
      uiHelpers.changeItemsPerPage(itemsPerPage, false);
      goToPage(1);
    };

    const seoMeta = computed(() => {
      if (route.value.path === `/${Routes.SEARCH}`) {
        return {
          title: `${i18n.t('Search results for: ')}'${route.value.query[searchTermQueryParameter]}'`,
          description: i18n.t('The largest bicycle shop in Austria'),
        };
      }

      // BK-225: category may be null here
      // once it's available, the function will run again and set the correct metadata
      if (!activeCategory.value) {
        return {};
      }

      const {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        meta_title, meta_description, meta_keywords, name, description, image,
      } = activeCategory.value;
      const title = meta_title ?? name;
      const categoryDescription = meta_description ?? description;

      const isManufacturerFilterActive = manufacturerNames.value?.length;
      const manufacturer = isManufacturerFilterActive ? manufacturerNames.value.join(',') : null;
      return {
        title,
        description: categoryDescription,
        og_title: title,
        twitter_title: title,
        og_description: categoryDescription,
        twitter_description: categoryDescription,
        og_image: image,
        twitter_image: image,
        keywords: meta_keywords,
        manufacturer,
        is_manufacturer_category: isManufacturerFilterActive,
      };
    });

    return {
      isPriceLoaded,
      ...uiHelpers,
      toggleFilterSidebar,
      isFilterSidebarOpen,
      addItemToCart,
      pagination,
      products,
      sortBy,
      heading,
      activeCategory,
      activeCategoryId,
      doChangeItemsPerPage,
      productContainerElement,
      categoryMeta,
      onReloadProducts,
      goToPage,
      filtersSliders,
      filters,
      fetch,
      showMatchingBikes,
      isBikeCategory,
      seoMeta,
      isMobile,
      activeSizeFilterValues,
    };
  },
  mounted() {
    this.isMobile = window.innerWidth < 1150;
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 1150;
    });
  },
});
